<script lang="ts">
	//Hooks
	import { createEventDispatcher } from 'svelte';
	//Helpers
	import { buildWebhookLabels } from 'src/helpers/actions/webhooks/view/webhook_view_helpers';
	import { classMap } from '@Shelly/shelly-design-system/helpers/component-utils';
	//Services
	import { format } from 'src/services/i18n';
	//Design system components
	import {
		ActionsContainer,
		AdvancedCardV2,
		Button,
		CardStatusBar,
		Container,
		IconButton,
		LabelListV2,
		Modal,
		Text,
		Toggle,
	} from '@Shelly/shelly-design-system';
	//Types
	import type { WebHooksParams } from 'src/types/requests/settings/webhooks';

	export let list: WebHooksParams[] = [],
		text = $format('empty_list'),
		showActionForAllChannels = false,
		removeLastSpacing = false;

	const dispatcher = createEventDispatcher(),
		deleteWebhook = () => {
			deleteWebhookModal = false;
			dispatcher('delete', selectedWebhook);
		};

	let deleteWebhookModal = false,
		selectedWebhook;
</script>

{#if list.length > 0}
	{#each list as hook, i}
		<AdvancedCardV2
			size="m"
			class={classMap({
				'mb-s': i < list.length - 1 || (i >= list.length - 1 && !removeLastSpacing),
			})}
			title={hook.name}
			subtitle={hook?.subtitle ? { value: hook.subtitle, bold: false } : null}
			hideSlotStatusBar={!showActionForAllChannels}
			leftSlotBackground
		>
			<CardStatusBar
				slot="statusBar"
				statsList={[
					{
						icon:
							hook.inputType === 'count|freq'
								? hook.icon
								: hook.cid < 100
									? 'far fa-microchip'
									: hook.cid < 200
										? 'far fa-puzzle-piece'
										: 'far fa-cube',
						// @ts-ignore
						iconColor:
							hook.inputType === 'count|freq'
								? 'secondary'
								: hook.cid < 100
									? 'info'
									: hook.cid < 200
										? 'warning'
										: 'success',
						text:
							hook.inputType === 'count|freq'
								? $format('settings_title_plus_uni_counter', {
										default: 'Plus Uni Counter',
									})
								: hook.cid < 100
									? hook?.deviceName || `${hook.type}:${hook.cid}`
									: hook.cid < 200
										? $format('label_peripheral', {
												default: 'Peripheral component',
											})
										: $format('label_virtual_component', {
												default: 'Virtual component',
											}),
					},
				]}
			/>
			<Toggle
				aria-label={$format('toggle_webhook', { default: 'Toggle webhook' })}
				slot="left"
				size="m"
				checked={hook.enable}
				on:click={() => {
					hook.enable = !hook.enable;
					dispatcher('toggle', hook);
				}}
				id="toggle-1nnzt8m-v2"
			/>
			<LabelListV2
				size="m"
				labelList={buildWebhookLabels(hook).map((i) => ({
					...i,
					type: hook.enable ? null : 'disabled',
				}))}
				slot="main"
				a11yProps={{ isInteractive: false }}
				data-prevent-drawer-swipe
				data-prevent-sidepanel-swipe
			/>
			<ActionsContainer orientation="vertical" type="simple" slot="right">
				<IconButton
					aria-label={$format('button_edit_webhook', {
						default: 'Button edit webhook',
					})}
					variant="primary"
					icon="fas fa-pen"
					slot="first"
					on:click={() => dispatcher('edit', { ...hook, index: i })}
				/>
				<IconButton
					aria-label={$format('button_open_modal_delete_webhook', {
						default: 'Button open modal delete webhook',
					})}
					icon="fas fa-trash"
					variant="danger"
					slot="second"
					on:click={() => {
						selectedWebhook = { index: i, id: hook.id, name: hook.name };
						deleteWebhookModal = true;
					}}
				/>
			</ActionsContainer>
		</AdvancedCardV2>
	{/each}
	<Modal
		showModal={deleteWebhookModal}
		on:close={() => (deleteWebhookModal = false)}
		title={$format('button_delete', { default: 'Delete' })}
		><svelte:fragment slot="content">
			<Container padding="0">
				<Text class="mb-s">
					{$format('on_delete_msg', {
						values: { this_type: selectedWebhook.name },
						default: 'Are you sure you want to delete {this_type}?',
					})}
				</Text>
			</Container>
		</svelte:fragment>
		<div class="d-flex justify-content-end" slot="footer">
			<Button
				class="min-width-md-m min-width-sm-auto width-md-auto width-sm-full text-nowrap mr-s"
				responsiveSize={{
					sm: 'm',
				}}
				variant="secondary"
				on:click={() => (deleteWebhookModal = false)}
			>
				{$format('button_cancel', { default: 'Cancel' })}
			</Button>
			<Button
				class="min-width-md-m min-width-sm-auto width-md-auto width-sm-full text-nowrap"
				variant="danger"
				responsiveSize={{
					sm: 'm',
				}}
				on:click={deleteWebhook}
			>
				{$format('button_delete', { default: 'Delete' })}
			</Button>
		</div>
	</Modal>
{:else}
	<Text class="mb-s">{text}</Text>
{/if}
